import axios from 'axios';
import { ClassId, ContentType, AspectRatio } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL;

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

export const generateText = async (prompt: string, classId: ClassId, contentType: ContentType) => {
  const response = await api.post('/text/generate', {
    prompt,
    class_id: classId,
    content_type: contentType,
  });
  return response.data.response;
};

export const generateImage = async (prompt: string, classId: ClassId, aspectRatio: AspectRatio) => {
  const response = await api.post('/image/generate', {
    prompt,
    class_id: classId,
    aspect_ratio: aspectRatio,
  });
  return response.data.image_url;
};

// 세션 기반 텍스트 생성 API
export const generateTextSession = async (prompt: string, sessionId: string, userNumber: number, contentType: ContentType) => {
  const response = await api.post('/text/generate-session', {
    prompt,
    session_id: sessionId,
    user_number: userNumber,
    content_type: contentType,
  });
  return response.data.response;
};

// 세션 기반 이미지 생성 API
export const generateImageSession = async (prompt: string, sessionId: string, userNumber: number, aspectRatio: AspectRatio) => {
  const response = await api.post('/image/generate-session', {
    prompt,
    session_id: sessionId,
    user_number: userNumber,
    aspect_ratio: aspectRatio,
  });
  return response.data.image_url;
};

// 클래스 코드 관리 API (Backend 스키마에 맞게 수정됨)
export interface ClassCodeResponse {
  id: string;
  class_name: string;
  description?: string;
  access_code: string;
  max_students: number;
  student_count: number;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
}

export interface ClassCodeCreate {
  class_name: string;
  description?: string;
  max_students?: number;
  is_active?: boolean;
}

export const createClassCode = async (classData: ClassCodeCreate): Promise<ClassCodeResponse> => {
  const response = await api.post('/class-codes/', classData);
  return response.data;
};

export const getAllClassCodes = async (): Promise<ClassCodeResponse[]> => {
  const response = await api.get('/class-codes/');
  return response.data;
};

export const verifyAccessCode = async (accessCode: string) => {
  const response = await api.post('/class-codes/verify', {
    access_code: accessCode,
    student_name: '테스트 학생'
  });
  return response.data;
};

// ===== 새로운 액세스 코드 API =====

// 액세스 코드 생성
export const createAccessCode = async (data: {
  code: string;
  name?: string;
  max_users?: number;
  max_video_generations?: number;
}) => {
  try {
    const response = await api.post('/access-codes/', data);
    return response.data;
  } catch (error) {
    console.error('Failed to create access code:', error);
    throw error;
  }
};

// 모든 액세스 코드 조회
export const getAllAccessCodes = async () => {
  try {
    const response = await api.get('/access-codes/');
    return response.data;
  } catch (error) {
    console.error('Failed to get access codes:', error);
    throw error;
  }
};

// 액세스 코드 업데이트
export const updateAccessCode = async (codeId: string, data: any) => {
  try {
    const response = await api.put(`/access-codes/${codeId}`, data);
    return response.data;
  } catch (error) {
    console.error('Failed to update access code:', error);
    throw error;
  }
};

// 액세스 코드 삭제
export const deleteAccessCode = async (codeId: string) => {
  try {
    const response = await api.delete(`/access-codes/${codeId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to delete access code:', error);
    throw error;
  }
};

// 액세스 코드로 연결
export const connectWithAccessCode = async (code: string) => {
  try {
    const response = await api.post('/access-codes/connect', null, {
      params: { code }
    });
    return response.data;
  } catch (error) {
    console.error('Failed to connect with access code:', error);
    throw error;
  }
};

// 사용자 연결 해제
export const disconnectUser = async (accessId: string) => {
  try {
    const response = await api.post(`/access-codes/disconnect/${accessId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to disconnect user:', error);
    throw error;
  }
};

// 액세스 코드 확인
export const verifyNewAccessCode = async (code: string) => {
  try {
    const response = await api.post('/access-codes/verify', null, {
      params: { code }
    });
    return response.data;
  } catch (error) {
    console.error('Failed to verify access code:', error);
    throw error;
  }
};

// 활성 사용자 조회
export const getActiveUsers = async (code: string) => {
  try {
    console.log('API 호출 시작 - 활성 사용자 조회:', code);
    console.log('요청 URL:', `/access-codes/${code}/users`);
    const response = await api.get(`/access-codes/${code}/users`);
    console.log('API 응답 성공:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('API 호출 실패 - 활성 사용자 조회:', {
      code,
      error: error.message,
      response: error.response,
      status: error.response?.status,
      data: error.response?.data
    });
    throw error;
  }
};

// 액세스 코드 기반 텍스트 생성
export const generateTextAccess = async (
  prompt: string,
  accessId: string,
  contentType: string = "Normal"
): Promise<string> => {
  try {
    const response = await api.post('/text/generate-access', {
      prompt,
      access_id: accessId,
      content_type: contentType
    });
    return response.data.response;
  } catch (error) {
    console.error('Failed to generate text with access code:', error);
    throw error;
  }
};

// 액세스 코드 기반 이미지 생성
export const generateImageAccess = async (
  prompt: string,
  accessId: string,
  aspectRatio: string = "1:1"
): Promise<string> => {
  try {
    const response = await api.post('/image/generate-access', {
      prompt,
      access_id: accessId,
      aspect_ratio: aspectRatio
    });
    return response.data.image_url;
  } catch (error) {
    console.error('Failed to generate image with access code:', error);
    throw error;
  }
};

// 액세스 코드 기반 영상 생성
export const generateVideoAccess = async (
  imageData: string,
  promptText: string,
  accessId: string,
  ratio: string = '1280:720',
  duration: number = 5
): Promise<VideoTaskResponse> => {
  try {
    const response = await api.post('/video/generate-access', {
      image_data: imageData,
      prompt_text: promptText,
      access_id: accessId,
      ratio: ratio,
      duration: duration
    });
    return response.data;
  } catch (error) {
    console.error('Failed to generate video with access code:', error);
    throw error;
  }
};

// 사용자의 영상 생성 횟수 정보 조회
export const getUserVideoGenerationInfo = async (accessId: string): Promise<{
  current_count: number;
  max_count: number;
  remaining_count: number;
}> => {
  try {
    const response = await api.get(`/access-codes/user/${accessId}/video-info`);
    return response.data;
  } catch (error) {
    console.error('Failed to get user video generation info:', error);
    throw error;
  }
};

// ===== 비디오 생성 API =====

// 비디오 생성 응답 인터페이스
export interface VideoTaskResponse {
  success: boolean;
  task_id?: string;
  video_url?: string;
  status?: string;
  prompt_text?: string;
  ratio?: string;
  duration?: number;
  error?: string;
  details?: string;
}

// 이미지에서 비디오 생성
export const generateVideoFromImage = async (
  imageData: string,
  promptText: string,
  ratio: string = '1280:720',
  duration: number = 5
): Promise<VideoTaskResponse> => {
  try {
    const response = await api.post('/video/generate-image-to-video', {
      image_data: imageData,
      prompt_text: promptText,
      ratio,
      duration,
    });
    return response.data;
  } catch (error) {
    console.error('Failed to generate video from image:', error);
    throw error;
  }
};

// 세션 기반 비디오 생성
export const generateVideoSession = async (
  imageData: string,
  promptText: string,
  sessionId: string,
  userId: string,
  ratio: string = '1280:720',
  duration: number = 5
): Promise<VideoTaskResponse> => {
  try {
    const response = await api.post('/video/generate-session', {
      image_data: imageData,
      prompt_text: promptText,
      session_id: sessionId,
      user_id: userId,
      ratio,
      duration,
    });
    return response.data;
  } catch (error) {
    console.error('Failed to generate video in session:', error);
    throw error;
  }
};

// 비디오 생성 작업 상태 조회
export const getVideoTaskStatus = async (taskId: string): Promise<VideoTaskResponse> => {
  try {
    const response = await api.get(`/video/task-status/${taskId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to get video task status:', error);
    throw error;
  }
};

// AI 생기부 생성 API
export interface AiRecordRequest {
  subject: string;
  assignment: string;
  student_result: string;
  custom_fields: Record<string, string>;
  user_email: string;
}

export interface AiRecordResponse {
  generated_content: string;
  char_count: number;
  success: boolean;
  error?: string;
}

export const generateAiRecord = async (data: AiRecordRequest): Promise<AiRecordResponse> => {
  try {
    const response = await api.post('/ai-record/generate-record', data);
    return response.data;
  } catch (error) {
    console.error('Failed to generate AI record:', error);
    throw error;
  }
};

// 작업공간 저장/불러오기 API
export interface SaveWorkspaceRequest {
  user_email: string;
  workspace_name: string;
  records: any[];
  custom_columns: any[];
}

export interface WorkspaceResponse {
  success: boolean;
  workspace_id?: string;
  workspace?: any;
  error?: string;
}

export const saveWorkspace = async (data: SaveWorkspaceRequest): Promise<WorkspaceResponse> => {
  try {
    const response = await api.post('/ai-record/save-workspace', data);
    return response.data;
  } catch (error) {
    console.error('Failed to save workspace:', error);
    throw error;
  }
};

export const loadWorkspace = async (userEmail: string): Promise<WorkspaceResponse> => {
  try {
    const response = await api.get(`/ai-record/load-workspace/${encodeURIComponent(userEmail)}`);
    return response.data;
  } catch (error) {
    console.error('Failed to load workspace:', error);
    throw error;
  }
}; 